import type { CodeSession } from './mock-sessions';

/** Compact relative label the code sidebar shows (`now`, `4m`, `2h`, `3d`). */
export function relativeWhen(isoDate: string, now: number = Date.now()): string {
  const minutes = Math.floor((now - new Date(isoDate).getTime()) / 60_000);
  if (minutes < 1) {
    return 'now';
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
}

/** `org/repo` → `repo`, as the code sidebar labels a session's repository. */
export function shortRepoName(fullName: string): string {
  const [, name = fullName] = fullName.split('/');
  return name;
}

const NO_LABELS: ReadonlyMap<number, string> = new Map();

/**
 * The label the sidebar's rows show for each repository, by repository ID, so a renamed
 * repository's rows agree: its short current name, or its full name where two of the repositories
 * share a short one (`org-a/api`, `org-b/api`). None while the sessions are all in one repository,
 * where every row would repeat the same label.
 *
 * Pass every active session and the archived ones the sidebar read, rather than a search's active
 * matches: a search that narrows to one repository would otherwise take every label away while
 * someone types.
 */
export function repoLabelsFor(
  sessions: readonly Pick<CodeSession, 'repo' | 'repositoryRef'>[],
  canonicalNames: ReadonlyMap<number, string>,
): ReadonlyMap<number, string> {
  const names = new Map<number, string>();
  for (const { repo, repositoryRef } of sessions) {
    const id = repositoryRef.repositoryId;
    if (!names.has(id)) {
      // A session's repository by its current name, the one a new session starts in.
      names.set(id, canonicalNames.get(id) ?? repo);
    }
  }
  if (names.size < 2) {
    return NO_LABELS;
  }
  const repos = [...names].map(([id, name]) => ({ id, name, shortName: shortRepoName(name) }));
  const shortNameCounts = new Map<string, number>();
  for (const { shortName } of repos) {
    shortNameCounts.set(shortName, (shortNameCounts.get(shortName) ?? 0) + 1);
  }
  return new Map(
    repos.map(({ id, name, shortName }) => [
      id,
      shortNameCounts.get(shortName) === 1 ? shortName : name,
    ]),
  );
}
